import React, { useState, useEffect } from 'react';
import { MessageSquare, Search, ChevronRight, Clock, Check } from 'lucide-react';
import { collection, query, orderBy, limit, getDocs, doc, getDoc, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import logger from '../../utils/logger';

const InboxView = ({ userId, friends, onOpenChat, friendStatuses }) => {
    const [lastMessages, setLastMessages] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    // Real-time listener for last messages per friend (was 10s polling)
    useEffect(() => {
        if (!userId || friends.length === 0) return;

        const unsubscribers = [];

        for (const friend of friends) {
            if (!friend.uid) continue;

            const threadId = [userId, friend.uid].sort().join('_');
            const q = query(
                collection(db, FIRESTORE_PATHS.CHATS, threadId, FIRESTORE_PATHS.MESSAGES),
                orderBy('createdAt', 'desc'),
                limit(1)
            );

            const unsub = onSnapshot(q, (snap) => {
                if (!snap.empty) {
                    setLastMessages(prev => ({ ...prev, [friend.uid]: snap.docs[0].data() }));
                }
            }, (err) => {
                logger.error('Inbox', `Error listening to thread for ${friend.uid}:`, err);
            });

            unsubscribers.push(unsub);
        }

        return () => unsubscribers.forEach(unsub => unsub());
    }, [userId, friends]);

    const isOnline = (lastActive) => {
        if (!lastActive) return false;
        try {
            const now = new Date();
            const activeTime = lastActive.toDate ? lastActive.toDate() : new Date(lastActive);
            const diffInSeconds = (now - activeTime) / 1000;
            // Be lenient with clock drift - 5 minutes
            return diffInSeconds < 300; 
        } catch (err) {
            return false;
        }
    };

    const sortedFriends = [...friends].sort((a, b) => {
        const timeA = lastMessages[a.uid]?.createdAt?.toDate?.() || 0;
        const timeB = lastMessages[b.uid]?.createdAt?.toDate?.() || 0;
        return timeB - timeA;
    });

    const filteredFriends = sortedFriends.filter(f => 
        f.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex-1 flex flex-col bg-gray-900 overflow-hidden">
            {/* Header */}
            <div className="p-4 bg-gray-800 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white mb-4">Inbox</h2>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input
                        type="text"
                        placeholder="Search conversations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-gray-900 border border-gray-700 rounded-xl py-2 pl-10 pr-4 text-white text-sm focus:outline-none focus:border-neon-cyan"
                    />
                </div>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
                {filteredFriends.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 px-8 text-center">
                        <MessageSquare size={48} className="mb-4 opacity-20" />
                        <p>No conversations found.</p>
                        <p className="text-xs mt-2">Start a chat with your companions from the "Companions" tab!</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-800">
                        {filteredFriends.map(friend => {
                            const lastMsg = lastMessages[friend.uid];
                            const online = isOnline(friendStatuses[friend.uid]);
                            const hasUnread = lastMsg && lastMsg.senderId !== userId && lastMsg.status !== 'seen';

                            return (
                                <button
                                    key={friend.uid}
                                    onClick={() => onOpenChat(friend)}
                                    className={`w-full p-4 flex items-center gap-4 transition-colors text-left ${hasUnread ? 'bg-blue-500/5' : 'hover:bg-gray-800/50'}`}
                                >
                                    <div className="relative">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-bold text-white shadow-lg">
                                            {friend.displayName?.charAt(0) || '?'}
                                        </div>
                                        <div className={`absolute bottom-0 right-0 w-3.5 h-3.5 border-2 border-gray-900 rounded-full ${online ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <h3 className={`font-bold truncate ${hasUnread ? 'text-white' : 'text-gray-200'}`}>{friend.displayName}</h3>
                                            {lastMsg?.createdAt && (
                                                <span className={`text-[10px] whitespace-nowrap ${hasUnread ? 'text-neon-cyan font-bold' : 'text-gray-500'}`}>
                                                    {lastMsg.createdAt.toDate ? new Date(lastMsg.createdAt.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <p className={`text-xs truncate flex-1 ${hasUnread ? 'text-gray-200 font-medium' : 'text-gray-500'}`}>
                                                {lastMsg ? (
                                                    lastMsg.senderId === userId ? `You: ${lastMsg.text}` : lastMsg.text
                                                ) : (
                                                    'No messages yet'
                                                )}
                                            </p>
                                            {lastMsg && lastMsg.senderId === userId && (
                                                <div className="flex items-center">
                                                    {lastMsg.status === 'seen' ? (
                                                        <div className="flex -space-x-1.5 text-sky-400">
                                                            <Check size={12} strokeWidth={3} />
                                                            <Check size={12} strokeWidth={3} />
                                                        </div>
                                                    ) : lastMsg.status === 'delivered' ? (
                                                        <div className="flex -space-x-1.5 text-gray-500">
                                                            <Check size={12} strokeWidth={3} />
                                                            <Check size={12} strokeWidth={3} />
                                                        </div>
                                                    ) : (
                                                        <Check size={12} strokeWidth={3} className="text-gray-500" />
                                                    )}
                                                </div>
                                            )}
                                            {hasUnread && (
                                                <div className="w-2 h-2 bg-neon-cyan rounded-full"></div>
                                            )}
                                        </div>
                                    </div>
                                    <ChevronRight size={16} className="text-gray-700" />
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InboxView;




